import { Component, Vue } from 'vue-property-decorator';
import { AutoApprovalErrorHandler, UserFriendlyError } from '@/services/AutoApprovalErrorHandler';

@Component
export default class AutoApprovalErrorHandlingMixin extends Vue {
  // Состояние загрузки для различных операций
  protected loadingStates: { [key: string]: boolean } = {};
  
  // Состояние ошибок
  protected errorStates: { [key: string]: UserFriendlyError | null } = {};
  
  // Состояние успешных операций
  protected successStates: { [key: string]: { title: string; message: string; details?: string[] } | null } = {};

  /**
   * Устанавливает состояние загрузки для операции
   */
  protected setLoading(operation: string, loading: boolean): void {
    this.$set(this.loadingStates, operation, loading);
  }

  /**
   * Проверяет, выполняется ли операция
   */
  protected isLoading(operation: string): boolean {
    return this.loadingStates[operation] || false;
  }

  /**
   * Обрабатывает ошибку для операции
   */
  protected handleError(operation: string, error: any): void {
    const userError = AutoApprovalErrorHandler.handleError(error);
    this.$set(this.errorStates, operation, userError);
    
    // Показываем Toast уведомление
    this.showErrorToast(userError);
    
    // Сбрасываем состояние загрузки
    this.setLoading(operation, false);
  }

  /**
   * Очищает ошибку для операции
   */
  protected clearError(operation: string): void {
    this.$set(this.errorStates, operation, null);
  }

  /**
   * Получает ошибку для операции
   */
  protected getError(operation: string): UserFriendlyError | null {
    return this.errorStates[operation] || null;
  }

  /**
   * Устанавливает сообщение об успехе для операции
   */
  protected setSuccess(operation: string, title: string, message: string, details?: string[]): void {
    this.$set(this.successStates, operation, { title, message, details });
    
    // Показываем Toast уведомление
    this.showSuccessToast(title, message);
    
    // Автоматически скрываем через 5 секунд
    setTimeout(() => {
      this.clearSuccess(operation);
    }, 5000);
  }

  /**
   * Очищает сообщение об успехе для операции
   */
  protected clearSuccess(operation: string): void {
    this.$set(this.successStates, operation, null);
  }

  /**
   * Получает сообщение об успехе для операции
   */
  protected getSuccess(operation: string): { title: string; message: string; details?: string[] } | null {
    return this.successStates[operation] || null;
  }

  /**
   * Выполняет операцию с обработкой ошибок
   */
  protected async executeWithErrorHandling<T>(
    operation: string,
    asyncFunction: () => Promise<T>,
    successTitle?: string,
    successMessage?: string
  ): Promise<T | null> {
    try {
      this.setLoading(operation, true);
      this.clearError(operation);
      this.clearSuccess(operation);

      const result = await asyncFunction();

      if (successTitle && successMessage) {
        this.setSuccess(operation, successTitle, successMessage);
      }

      return result;
    } catch (error) {
      this.handleError(operation, error);
      return null;
    } finally {
      this.setLoading(operation, false);
    }
  }

  /**
   * Повторяет операцию
   */
  protected async retryOperation<T>(
    operation: string,
    asyncFunction: () => Promise<T>,
    successTitle?: string,
    successMessage?: string
  ): Promise<T | null> {
    const error = this.getError(operation);
    if (!error || !AutoApprovalErrorHandler.isRetryable(error)) {
      return null;
    }

    return this.executeWithErrorHandling(operation, asyncFunction, successTitle, successMessage);
  }

  /**
   * Показывает Toast уведомление об ошибке
   */
  private showErrorToast(error: UserFriendlyError): void {
    const toastMessage = AutoApprovalErrorHandler.createToastMessage(error);
    
    // Используем существующий Toast миксин, если он доступен
    if (this.$message) {
      this.$message.error({
        content: `${toastMessage.title}: ${toastMessage.message}`,
        duration: toastMessage.duration / 1000
      });
    } else {
      // Fallback для случаев, когда Toast миксин недоступен
      console.error('Error Toast:', toastMessage);
    }
  }

  /**
   * Показывает Toast уведомление об успехе
   */
  private showSuccessToast(title: string, message: string): void {
    if (this.$message) {
      this.$message.success({
        content: `${title}: ${message}`,
        duration: 3
      });
    } else {
      console.log('Success Toast:', { title, message });
    }
  }

  /**
   * Проверяет, есть ли активные операции загрузки
   */
  protected get hasActiveLoading(): boolean {
    return Object.values(this.loadingStates).some(loading => loading);
  }

  /**
   * Проверяет, есть ли активные ошибки
   */
  protected get hasActiveErrors(): boolean {
    return Object.values(this.errorStates).some(error => error !== null);
  }

  /**
   * Проверяет, есть ли активные сообщения об успехе
   */
  protected get hasActiveSuccess(): boolean {
    return Object.values(this.successStates).some(success => success !== null);
  }

  /**
   * Очищает все состояния ошибок и успехов
   */
  protected clearAllStates(): void {
    Object.keys(this.errorStates).forEach(key => {
      this.clearError(key);
    });
    Object.keys(this.successStates).forEach(key => {
      this.clearSuccess(key);
    });
  }
}